import { useEffect } from "react";
import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { io, type Socket } from "socket.io-client";
import { create } from "zustand";
import { useAuth } from "./auth";

/**
 * 콘솔 실시간 채널 (gateway /console). 서버는 "무엇이 바뀌었는지" 신호만 보내고,
 * 여기서는 그 신호에 맞는 화면 데이터를 다시 읽게(invalidate) 한다. 내용은 항상 API 가 걸러서 준다.
 * 끊기면 각 화면의 주기 갱신이 짧아져 그대로 따라잡는다 (queries.ts 의 live/slow).
 */
type Signal = { topic: "task" | "run" | "machine" | "customer" | "billing" | "audit"; customerId: string | null; machineId: string | null; taskId: string | null; runId: string | null; action: string | null };

export const useRealtime = create<{ connected: boolean; lastEventAt: number | null }>(() => ({ connected: false, lastEventAt: null }));
export const isRealtime = () => useRealtime.getState().connected;

/** 신호 → 다시 읽을 화면 데이터. 키는 queries.ts 의 keys 와 같은 앞부분을 쓴다 (앞부분만 맞으면 모두 무효화). */
function keysFor(s: Signal): QueryKey[] {
  const c = s.customerId;
  const k: QueryKey[] = [];
  switch (s.topic) {
    case "task":
    case "run":
      if (c) k.push(["c", c, "tasks"], ["c", c, "runs"], ["c", c, "summary"], ["c", c, "usage"], ["c", c, "jobs"]);
      if (c && s.taskId) k.push(["c", c, "task", s.taskId]);
      if (s.runId) k.push(["run"]);
      if (c && s.machineId) k.push(["c", c, "machine", s.machineId]);
      k.push(["admin", "runs"], ["admin", "overview"], ["admin", "results"], ["stats"]);
      break;
    case "machine":
      if (c) k.push(["c", c, "machines"], ["c", c, "devices"], ["c", c, "summary"], ["c", c, "status"]);
      if (c && s.machineId) k.push(["c", c, "machine", s.machineId]);
      if (s.machineId) k.push(["machine-state", s.machineId]);
      k.push(["admin", "machines"], ["admin", "overview"], ["status-history"], ["stats"]);
      break;
    case "customer":
    case "billing":
    case "audit":
      if (c) k.push(["c", c], ["billing", c], ["seo", c]);
      k.push(["admin"], ["billing", "admin"], ["seo", "admin"]);
      break;
  }
  return k;
}

/** 로그인 상태를 따라 소켓을 열고 닫는 훅 (Root 에서 한 번). */
export function useRealtimeConnection(queryClient: QueryClient): void {
  const token = useAuth((s) => s.token);
  const status = useAuth((s) => s.status);
  useEffect(() => {
    if (status !== "signed-in" || !token) return;
    let pending = new Map<string, QueryKey>();
    let flush: ReturnType<typeof setTimeout> | undefined;
    const socket: Socket = io("/console", {
      path: "/socket.io",
      transports: ["websocket"],
      // 재연결할 때마다 가장 최근 액세스 토큰을 쓴다 (15분마다 갱신됨).
      auth: (cb) => cb({ token: useAuth.getState().token }),
      reconnectionDelay: 1000,
      reconnectionDelayMax: 15_000,
    });
    socket.on("console.ready", () => useRealtime.setState({ connected: true }));
    socket.on("disconnect", () => useRealtime.setState({ connected: false }));
    socket.on("connect_error", () => useRealtime.setState({ connected: false }));
    socket.on("console.event", (s: Signal) => {
      useRealtime.setState({ lastEventAt: Date.now() });
      for (const key of keysFor(s)) pending.set(JSON.stringify(key), key);
      // 진행 로그처럼 몰려오는 신호는 묶어서 한 번에 다시 읽는다.
      clearTimeout(flush);
      flush = setTimeout(() => {
        const batch = pending;
        pending = new Map();
        for (const key of batch.values()) void queryClient.invalidateQueries({ queryKey: key });
      }, 250);
    });
    return () => {
      clearTimeout(flush);
      socket.close();
      useRealtime.setState({ connected: false });
    };
    // 토큰이 바뀌어도 연결은 유지한다 (auth 콜백이 재연결 때 새 토큰을 읽음). 로그인 주체가 바뀌면 status 가 바뀌며 다시 연다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, queryClient, status === "signed-in" ? useAuth.getState().me?.accountId : null]);
}
